import Highcharts from 'highcharts';
import { isEmpty } from 'lodash';
import { action, makeAutoObservable, runInAction } from 'mobx';

import type { SelectedWorkspace } from '../../../services/dashboardsService/hostContext';
import type { ITocOption } from '../../../services/intakeDataService/types';
import { getDate } from '../../../services/timeService';
import { dateTimeFormat } from '../../../services/timeService/types';
import type {
  DashboardTab,
  GlobalFilters,
  IAdminDashboardStore,
  OutcomesTimeline,
  StaticTabId,
  TabFilters,
  TabId,
} from './AdminDashboard.types';
import { dashboardTabId, DEFAULT_OUTCOMES_TIMELINE, TabIds } from './AdminDashboard.types';
import { PRESETS } from './components/DateRangePicker/OdsAdminDateRange.types';

export class AdminDashboardStore implements IAdminDashboardStore {
  workspaces: { displayName: string; value: string }[] = [];
  typeOfCare: ITocOption[] = [];
  careLocations: string[] = [];
  latestDataPoint: number | null = null;
  displayRtmUnitValue = true;
  activeDashboardDeclaresCohort = false;
  _allowedTabs = {
    [TabIds.Utilization]: true,
    [TabIds.FallRisk]: false,
    [TabIds.RTM]: false,
    [TabIds.Outcomes]: false,
  };
  dashboardTabs: DashboardTab[] = [];
  outcomesTimeline: OutcomesTimeline = DEFAULT_OUTCOMES_TIMELINE;
  filters: GlobalFilters & TabFilters = {
    workspacesId: [], //@todo change to map or object.
    cohortSlug: '',
    careType: [],
    careLocation: [],
    utilization: {
      value: PRESETS['last30'].value,
      fromDate: PRESETS['last30'].getRange?.().start ?? getDate(),
      toDate: PRESETS['last30'].getRange?.().end ?? getDate(),
    },
    rtmDate: getDate().startOf('month').format(dateTimeFormat.SERVER_DATE_FORMAT),
  };
  legend: Record<string, boolean> = {};

  readonly thisMonth = getDate().startOf('month').format(dateTimeFormat.SERVER_DATE_FORMAT);

  constructor() {
    makeAutoObservable(this);
  }

  setLegendItemVisibility(key: string, isVisible: boolean) {
    runInAction(() => {
      this.legend[key] = isVisible;
      Highcharts.charts.forEach((chart) => (chart ? this._toggleSingleChart(chart, key) : null));
    });
  }

  deleteLegendItem(key: string) {
    delete this.legend[key];
  }

  setDashboardTabs(tabs: DashboardTab[]) {
    runInAction(() => {
      this.dashboardTabs = tabs;
    });
  }

  get selectedWorkspaces(): SelectedWorkspace[] {
    const displayNameOf = new Map(
      this.workspaces.map(({ value, displayName }) => [value, displayName]),
    );
    return this.filters.workspacesId.map((uuid) => ({
      uuid,
      displayName: displayNameOf.get(uuid) ?? uuid,
    }));
  }

  get allowedTabs(): TabId[] {
    const staticTabs = Object.entries(this._allowedTabs)
      .filter(([, isAllowed]) => isAllowed)
      .map(([tab]) => tab as TabId);
    const dashboards = [...this.dashboardTabs]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((dashboard) => dashboardTabId(dashboard.id));
    return [...staticTabs, ...dashboards];
  }

  @action
  setLatestDataPoint(number: number | null) {
    this.latestDataPoint = number;
  }

  setMetaData(tabs: StaticTabId[], workspaces: { displayName: string; value: string }[]) {
    runInAction(() => {
      this._allowedTabs = {
        [TabIds.Utilization]: true,
        [TabIds.FallRisk]: tabs.includes(TabIds.FallRisk),
        [TabIds.RTM]: tabs.includes(TabIds.RTM),
        [TabIds.Outcomes]: tabs.includes(TabIds.Outcomes),
      };
      this.workspaces = workspaces ?? [];
    });
  }

  // Renamed for clarity
  setFilterWorkspaces(workspaces: string[]) {
    runInAction(() => {
      this.filters.workspacesId = workspaces;
    });
  }

  setCohortSlug(slug: string) {
    runInAction(() => {
      this.filters.cohortSlug = slug;
    });
  }

  setActiveDashboardDeclaresCohort(declared: boolean) {
    runInAction(() => {
      this.activeDashboardDeclaresCohort = declared;
    });
  }

  @action
  setFilterCareType(careType: string[]) {
    if (isEmpty(careType)) {
      this.filters.careType = [];
      return;
    }
    if (careType.every((type) => this.typeOfCare.some((toc) => toc.uuid === type))) {
      this.filters.careType = careType;
    } else {
      console.error(`Care type ${careType} is not in the list of type of care.`);
      return;
    }
  }

  @action
  setFilterCareLocation(careLocation: string[]) {
    if (isEmpty(careLocation)) {
      this.filters.careLocation = [];
      return;
    }
    if (careLocation.every((location) => this.careLocations.includes(location))) {
      this.filters.careLocation = careLocation;
    } else {
      console.error(`Care location ${careLocation} is not in the list of care locations.`);
      return;
    }
  }

  setOutcomesTimeline(timeline: OutcomesTimeline) {
    runInAction(() => {
      this.outcomesTimeline = timeline;
    });
  }

  setDisplayRtmUnitValue(value: boolean) {
    runInAction(() => {
      this.displayRtmUnitValue = value;
    });
  }

  _toggleSingleChart = (chart: Highcharts.Chart, key: string) => {
    if (chart) {
      if (chart.options.chart?.type == 'pie') {
        chart.series[0].points.forEach((point) => {
          if (point.name === key && point.visible !== this.legend?.[key]) {
            point.setVisible(this.legend?.[key]);
          }
        });
      } else {
        const series = chart.series.find((s) => s.name === key);
        if (series && series.visible !== this.legend?.[key]) {
          series.setVisible(this.legend?.[key]);
        }
      }
    }
  };
  resetStore() {
    this.dashboardTabs = [];
    this._allowedTabs = {
      [TabIds.Utilization]: true,
      [TabIds.FallRisk]: false,
      [TabIds.RTM]: false,
      [TabIds.Outcomes]: false,
    };
    this.displayRtmUnitValue = false;
    this.outcomesTimeline = DEFAULT_OUTCOMES_TIMELINE;
    this.activeDashboardDeclaresCohort = false;
    this.legend = {};
    this.filters = {
      rtmDate: getDate().startOf('month').format(dateTimeFormat.SERVER_DATE_FORMAT),
      workspacesId: [],
      cohortSlug: '',
      careType: [],
      careLocation: [],
      utilization: {
        value: PRESETS['last30'].value,
        fromDate: PRESETS['last30'].getRange?.().start ?? getDate(),
        toDate: PRESETS['last30'].getRange?.().end ?? getDate(),
      },
    };
  }
}
